import {Card, FormControl, openDialog} from 'platform/components';
import {VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {PreviewCustomerMessages, TemplateContextEnum} from '@dms/api/core';
import i18n from '@dms/i18n';

import {Nullish} from 'shared/web';

import {FormValues} from '../types/FormValues';
import {EmailPreview} from './EmailPreview';
import {TemplateBodyField} from './TemplateBodyField';

interface EmailChannelControlProps {
  control: FormControl<FormValues>;
  formApi: UseFormReturn<FormValues>;
  context: TemplateContextEnum | Nullish;
  customerMessageId: PreviewCustomerMessages;
}

export function EmailChannelControl(props: EmailChannelControlProps) {
  const isBodyOrSubjectError =
    isNotNil(props.formApi.formState.errors.emailContextTemplateBody) ||
    isNotNil(props.formApi.formState.errors.emailContextTemplateSubject);

  const [isEmailEnabled, isAutomatizationEnabled, emailContextTemplateBody] = useWatch({
    control: props.control,
    name: ['emailEnabled', 'enabled', 'emailContextTemplateBody'],
  });

  const handlePreview = () => {
    if (!props.context) {
      return;
    }

    const [body, subject] = props.formApi.getValues([
      'emailContextTemplateBody',
      'emailContextTemplateSubject',
    ]);

    const locale = localeByLanguage[i18n.language] ?? 'en-GB';

    openDialog(
      <EmailPreview
        body={body ?? ''}
        subject={subject ?? ''}
        locale={locale}
        customerMessageId={props.customerMessageId}
      />,
      {title: i18n.t('general.labels.emailPreview')}
    );
  };

  return (
    <Card
      isExpandable
      isClosedByDefault
      variant="inlineWhite"
      colorStripe={isBodyOrSubjectError ? 'red' : undefined}
      title={i18n.t('general.labels.email')}
      actions={[
        {
          type: 'button',
          variant: 'link',
          title: i18n.t('general.labels.emailPreview'),
          size: 'small',
          leftIcon: 'view',
          onClick: handlePreview,
          isDisabled: isNilOrEmpty(emailContextTemplateBody) || !props.context,
        },
      ]}
      control={{
        type: 'checkbox',
        value: isEmailEnabled,
        isDisabled: !isAutomatizationEnabled,
        onChange: (value) => {
          props.formApi.setValue('emailEnabled', value);

          if (!value) {
            props.formApi.clearErrors('emailContextTemplateBody');
            props.formApi.clearErrors('emailContextTemplateSubject');
            props.formApi.clearErrors('emailCredentialsId');
          }
        },
      }}
    >
      <VStack spacing={4}>
        <TemplateBodyField
          name="emailContextTemplateSubject"
          control={props.control}
          context={props.context}
          label={i18n.t('general.labels.subject')}
          isDisabled={!isEmailEnabled || !isAutomatizationEnabled}
          minHeight={8}
        />
        <TemplateBodyField
          control={props.control}
          name="emailContextTemplateBody"
          context={props.context}
          label={i18n.t('page.messageTemplates.labels.message')}
          isDisabled={!isEmailEnabled || !isAutomatizationEnabled}
          minHeight={25}
        />
      </VStack>
    </Card>
  );
}

const localeByLanguage: Record<string, string> = {
  en: 'en-GB',
  es: 'es-ES',
  fr: 'fr-FR',
  it: 'it-IT',
  nl: 'nl-NL',
  pl: 'pl-PL',
  cs: 'cs-CZ',
  de: 'de-DE',
  sk: 'sk-SK',
};
